import Div from "@jumbo/shared/Div/Div";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import {makeStyles} from "@mui/styles";
import MuiDatepicker from "app/pages/Docgen/components/common/mui-datepicker/MuiDatepicker";
import FormPreloader from "app/pages/Docgen/utils/FormLoader";
import {fetchContinuityType, fetchPriorApplicationStatus} from "app/utils/constants/fetchMappingData";
import {useFormik} from "formik";
import moment from "moment";
import {useEffect, useRef, useState} from "react";
import {useNavigate} from "react-router-dom";
import {toast} from "react-toastify";
import * as Yup from "yup";
import {get, post, put} from "../../../common/network";
import {VALIDATION_MESSAGE} from "../../../constants/index";
import {getUniqueId} from "../../../utils/CommonMethods";

const RelatedMattersForm = (props) => {
    const {removeFormFields, formIndex, setFormikObj, formNumber, priorApplicationData, setPriorApplicationData, modify, filteredCountries, setFilteredCountries, countries, continuityTypeMapping, priorApplicationStatusMapping} = props;

    const validationSchema = Yup.object({
        countryId: Yup.number().required(VALIDATION_MESSAGE.required),
        status: Yup.string().required(VALIDATION_MESSAGE.required),
        continuityType: Yup.string().required(VALIDATION_MESSAGE.required),
        sequence_no: Yup.number().nullable().typeError(VALIDATION_MESSAGE.number)
    });

    const useStyles = makeStyles(() => ({
        menuPaper: {
            backgroundColor: "white",
            color: "black"
        }
    }));

    const handleSearchChange = (event) => {
        const value = event.target.value;

        // Filter the countries based on the search term
        const filtered = countries.filter((country) =>
            country.name.toLowerCase().includes(value.toLowerCase())
        );
        setFilteredCountries(filtered);
    };

    const classes = useStyles();

    // Increment the formNumber by 1 to set sequence_no
    const nextFormNumber = formNumber + 1;

    // Added formik for form and fields handling
    // initial form fields value
    const initialValues = (modify && priorApplicationData?.[formIndex]) || {
        status: "",
        countryId: null,
        continuityType: "",
        number: "",
        accessCode: "",
        filingDate: null,
        issueDate: null,
        patentNumber: "",
        sequence_no: nextFormNumber
    };

    // Formik form configuration
    const formik = useFormik({
        initialValues: modify ? {...initialValues, countryId: initialValues?.country?.id || null} : initialValues,
        validationSchema: validationSchema,
        onSubmit: () => {/* Handle form submission */
        }
    });

    useEffect(() => {
        // providing the formik object to the parent
        setFormikObj((prevState) => {
            return {...prevState, [formIndex]: formik};
        });
    }, []);

    useEffect(() => {
        // Updating the formik value
        setPriorApplicationData(prevState => ({...prevState, [formIndex]: formik.values}));
    }, [formik.values]);

    const handleClearFilingDate = () => {
        formik.setFieldValue("filingDate", null); // Clear the date field
    };

    return (
        <Paper sx={{py: "12px", mb: "20px", backgroundColor: "white"}}>
            <header className="sub-form-header">
                <div className="heading-area">
                    <h5>RELATED MATTER - {formNumber + 1}</h5>
                </div>
                <div className="action-area">
                    <Button
                        type="button"
                        size="small"
                        variant="outlined"
                        color="error"
                        onClick={() => removeFormFields(formIndex)}
                        startIcon={<RemoveCircleOutlineIcon/>}
                    >
                        Remove
                    </Button>
                </div>
            </header>
            <form onSubmit={formik.handleSubmit}>
                <div className="container">
                    <h6 className="form-sub-heading">Details</h6>
                    <div className="row">
                        <div className="col-lg-4 col-md-6">
                            <div className="form-group">
                                <FormControl className="w-100" size="small">
                                    <InputLabel id="prior-application-status">Prior Application Status *</InputLabel>
                                    <Select
                                        MenuProps={{classes: {paper: classes.menuPaper}}}
                                        labelId="status"
                                        id="status"
                                        name="status"
                                        label="Prior Application Status"
                                        value={formik.values.status}
                                        onChange={e => {
                                            formik.handleChange(e);
                                        }}
                                        onBlur={formik.handleBlur}
                                        error={
                                            Boolean(formik.errors.status)
                                        }
                                        helperText={
                                            formik.errors.status
                                        }
                                    >
                                        {Object.entries(priorApplicationStatusMapping).map(([key]) => (
                                            <MenuItem key={key} value={key}>{key}</MenuItem>
                                        ))}
                                    </Select>
                                    {formik.touched.status &&
                                        formik.errors.status && (
                                            <div style={{color: "red"}}>
                                                {formik.errors.status}
                                            </div>
                                        )}
                                </FormControl>
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-6">
                            <div className="form-group">
                                <FormControl
                                    className="w-100" size="small"
                                    error={formik.touched.countryId && Boolean(formik.errors.countryId)}
                                >
                                    <InputLabel id="prior-country-label">Country *</InputLabel>
                                    <Select
                                        MenuProps={{classes: {paper: classes.menuPaper}}}
                                        labelId="prior-country-label"
                                        label="Prior Country *"
                                        id="prior-country-id"
                                        name="countryId"
                                        value={formik.values.countryId ?? null}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        renderValue={(selectedId) => {
                                            const selectedCountry = countries.find((country) => country.id === selectedId);
                                            return selectedCountry ? selectedCountry.name : "";
                                        }}
                                    >
                                        {/* Dynamically populate countries */}
                                        {countries.map((country) => (
                                            <MenuItem key={country.id} value={country.id}>
                                                {country.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {formik.touched.countryId && formik.errors.countryId && (
                                        <FormHelperText>{formik.errors.countryId}</FormHelperText>
                                    )}
                                </FormControl>
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-6">
                            <div className="form-group">
                                <FormControl className="w-100" size="small">
                                    <InputLabel id="role_type-label">Continuity Type *</InputLabel>
                                    <Select
                                        MenuProps={{classes: {paper: classes.menuPaper}}}
                                        labelId="role_type-label"
                                        id="continuityType"
                                        name="continuityType"
                                        label="Continuity Type"
                                        value={formik.values.continuityType}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={
                                            Boolean(formik.errors.continuityType)
                                        }
                                        helperText={
                                            formik.errors.continuityType
                                        }
                                    >
                                        {Object.entries(continuityTypeMapping).map(([key]) => (
                                            <MenuItem key={key} value={key}>{key}</MenuItem>
                                        ))}

                                    </Select>
                                    {formik.touched.continuityType && formik.errors.continuityType && (
                                        <div style={{color: "red"}}>
                                            {formik.errors.continuityType}
                                        </div>
                                    )}
                                </FormControl>
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-6">
                            <div className="form-group">
                                <TextField
                                    type="text"
                                    fullWidth
                                    size="small"
                                    id="number"
                                    name="number"
                                    label="Prior Application Number"
                                    value={formik.values.number}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.number && Boolean(formik.errors.number)}
                                    helperText={formik.touched.number && formik.errors.number}
                                />
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-6">
                            <div className="form-group">
                                <TextField
                                    type="text"
                                    fullWidth
                                    size="small"
                                    id="accessCode"
                                    name="accessCode"
                                    label="Access Code"
                                    value={formik.values.accessCode}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={
                                        formik.touched.accessCode &&
                                        Boolean(formik.errors.accessCode)
                                    }
                                    helperText={
                                        formik.touched.accessCode && formik.errors.accessCode
                                    }
                                />
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-6">
                            <div className="form-group">
                                <TextField
                                    type="text"
                                    fullWidth
                                    size="small"
                                    id="patentNumber"
                                    name="patentNumber"
                                    label="Prio Patent Number"
                                    value={formik.values.patentNumber}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.patentNumber && Boolean(formik.errors.patentNumber)}
                                    helperText={formik.touched.patentNumber && formik.errors.patentNumber}
                                />
                            </div>
                        </div>

                        <div className="col-lg-4 col-md-6">
                            <div className="form-group">
                                <Div
                                    sx={{
                                        position: "relative",

                                        ".reset-date-icon": {
                                            position: "absolute",
                                            top: 0,
                                            right: 0
                                        }
                                    }}
                                >
                                    <MuiDatepicker
                                        id="filingDate"
                                        name="filingDate"
                                        label="Filing Date"
                                        value={formik.values.filingDate}
                                        format="MM-DD-YYYY"
                                        onChange={(value) => {
                                            formik.setFieldValue("filingDate", moment(value));
                                        }}
                                        error={
                                            formik.touched.filingDate &&
                                            Boolean(formik.errors.filingDate)
                                        }
                                        helperText={
                                            formik.touched.filingDate && formik.errors.filingDate
                                        }
                                        renderInput={(params) => <TextField  {...params} />}
                                        maxDate={moment(new Date())}
                                    />
                                    <IconButton
                                        className="reset-date-icon"
                                        size="small"
                                        onClick={handleClearFilingDate}
                                        style={{
                                            margin: "4.5px 5px 0 0",
                                            color: "#8595A6"
                                        }}
                                    >
                                        <ClearIcon
                                            style={{
                                                fontSize: "16px"
                                            }}
                                        />
                                    </IconButton>
                                </Div>
                            </div>
                        </div>

                        <div className="col-lg-4 col-md-6">
                            <div className="form-group">
                                <Div
                                    sx={{
                                        position: "relative",

                                        ".reset-date-icon": {
                                            position: "absolute",
                                            top: 0,
                                            right: 0
                                        }
                                    }}
                                >
                                    <MuiDatepicker
                                        id="issueDate"
                                        name="issueDate"
                                        label="Issue Date"
                                        value={formik.values.issueDate}
                                        format="MM-DD-YYYY"
                                        onChange={(value) => {
                                            formik.setFieldValue("issueDate", moment(value));
                                        }}
                                        error={
                                            formik.touched.issueDate &&
                                            Boolean(formik.errors.issueDate)
                                        }
                                        helperText={
                                            formik.touched.issueDate && formik.errors.issueDate
                                        }
                                        renderInput={(params) => <TextField  {...params} />}
                                        maxDate={moment(new Date())}
                                    />
                                    <IconButton
                                        className="reset-date-icon"
                                        size="small"
                                        onClick={handleClearFilingDate}
                                        style={{
                                            margin: "4.5px 5px 0 0",
                                            color: "#8595A6"
                                        }}
                                    >
                                        <ClearIcon
                                            style={{
                                                fontSize: "16px"
                                            }}
                                        />
                                    </IconButton>
                                </Div>
                            </div>
                        </div>

                    </div>

                </div>
            </form>
        </Paper>
    );
};

const RelatedMatters = ({setActiveTab, priorApplicationData, setPriorApplicationData, formikObj, setFormikObj, handleClose, matterId, modify,combinedMatterData,setCombinedMatterData}) => {
    const navigate = useNavigate();

    const [filteredCountries, setFilteredCountries] = useState([]);
    const [countries, setCountries] = useState([]);
    const [continuityTypeMapping, setContinuityTypeMapping] = useState({});
    const [priorApplicationStatusMapping, setPriorApplicationStatusMapping] = useState({});
    const [loading, setLoading] = useState(false);
    const [cancelLoading, setCancelLoading] = useState(false);
    const [isFetching,setIsFetching] = useState(true);
    const isMountedRef = useRef(true);
    useEffect(() => () => {
        isMountedRef.current = false;
    }, []);

    const fetchCountryData = async () => {
        const data = await get("docgen/countries");
        return data;
    };

    useEffect(() => {
        const getMappings = async () => {
            try {
                const [continuityTypeResponse, priorApplicationStatusResponse, countryDataResponse] = await Promise.all([fetchContinuityType(), fetchPriorApplicationStatus(), fetchCountryData()]);
                setContinuityTypeMapping(continuityTypeResponse || {});
                setPriorApplicationStatusMapping(priorApplicationStatusResponse || {});
                setCountries(countryDataResponse || []);
                setFilteredCountries(countryDataResponse || []);
            } catch (error) {
            } finally {
                if (isMountedRef.current) {
                    setIsFetching(false);
                }
            }
        };
        getMappings();
    }, []);

    // Function for adding more fields
    const addFormFields = () => {
        // Adding a new form with a unique ID
        const id = getUniqueId();
        setFormikObj(prevState => ({...prevState, [id]: {}}));
    };

    // Function to remove the form fields
    const removeFormFields = formIndex => {
        setFormikObj((prevState) => {
            delete prevState[formIndex]; // Deleting the id
            return {...prevState};
        });
        setPriorApplicationData((prevState) => {
            delete prevState[formIndex]; // Deleting the id
            return {...prevState};
        });
    };

    // Function for handling final submission of the form
    const finalSubmitHandler = async handleCloseRequest => {
        try {
            let isValid = true;

            // Checking the validation for all the sub forms
            const validationPromises = Object.keys(formikObj)?.map(async (item) => {
                if (typeof formikObj[item]?.validateForm === "function") {
                    const validationObj = await formikObj[item]?.validateForm();

                    if (Object.keys(validationObj).length > 0) {
                        isValid = false;
                    }
                }
            });
            // Waiting for all messages to resolve
            await Promise.all(validationPromises);

            // if isValid is equal to true, then let the form submit
            if (isValid) {
                // Convert priorApplicationData to an array
                setLoading(true);
                const formatDate = date => {
                    if (!date) return null; // Return empty string if date is null or undefined

                    date = typeof date === "string" ? new Date(date + "T00:00:00") : new Date(date);
                    const month = date.getMonth() + 1; // Month is zero-indexed, so we add 1
                    const day = date.getDate();
                    const year = date.getFullYear();

                    // Pad single digit month and day with leading zero if needed
                    const formattedMonth = month < 10 ? `0${month}` : month;
                    const formattedDay = day < 10 ? `0${day}` : day;

                    // Format the date as MM-DD-YYYY
                    return `${formattedMonth}-${formattedDay}-${year}`;
                };

                const transformedData = Object.keys(priorApplicationData)?.map(key => {

                    const priorApplication = priorApplicationData[key];
                    if (priorApplication?.status !== "") {
                        return {
                            number: priorApplication?.number || "",
                            countryId: priorApplication?.countryId || priorApplication?.country?.id || null,
                            issueDate: formatDate(priorApplication?.issueDate) || null,
                            patentNumber: priorApplication?.patentNumber || "",
                            filingDate: formatDate(priorApplication?.filingDate),
                            continuityType: priorApplication?.continuityType || "",
                            status: priorApplication?.status || "",
                            accessCode: priorApplication?.accessCode || ""
                        };
                    }
                }).filter(Boolean);
                setCombinedMatterData((prevState)=> ({...prevState,priorApplications: transformedData}));

                let response;
                if (modify) {
                    response = await put(`docgen/matters/${matterId}`, {...combinedMatterData,priorApplications: transformedData});
                } else {
                    response = await post(`docgen/matters`, {...combinedMatterData,priorApplications: transformedData});
                }
                if (response?.success === true) {
                    toast.success(response.message);
                    navigate("/docgen/matters", {
                        replace: true,
                        state: { triggerRefresh: true },
                    });
                    if (modify) {
                        setActiveTab(4);
                    } else {
                        if (handleCloseRequest) {
                            handleClose();
                        }
                    }
                }
            } else {
                Object.keys(formikObj).map((item) => formikObj[item].submitForm());
            }
        } catch (error) {
        } finally {
            if (isMountedRef.current) {
                setLoading(false);
            }
        }
    };

    const handleCancel = async () => {
        setCombinedMatterData({});
        handleClose();
    }

    return (
        <>
            {isFetching ? <FormPreloader/> :
                <Div>
                    <div className="form-action-area container mb-0 px-0">
                        {cancelLoading ? <CircularProgress/> : <Button type="button" variant="outlined" color="error" onClick={handleCancel} sx={{marginRight: "1%"}}>Cancel</Button>}
                        {loading ? <CircularProgress/> : <Button type="button" variant="contained" className="btn-theme" onClick={() => finalSubmitHandler(true)}>Submit</Button>}
                    </div>
                    {Object.keys(formikObj).map((objKey, index) => (
                        <RelatedMattersForm
                            key={objKey}
                            removeFormFields={removeFormFields}
                            formIndex={objKey}
                            setFormikObj={setFormikObj}
                            formNumber={index}
                            priorApplicationData={priorApplicationData}
                            setPriorApplicationData={setPriorApplicationData}
                            modify={modify}
                            continuityTypeMapping={continuityTypeMapping}
                            priorApplicationStatusMapping={priorApplicationStatusMapping}
                            countries={countries}
                            filteredCountries={filteredCountries}
                            setFilteredCountries={setFilteredCountries}
                        />
                    ))}
                    <div className="sub-form-action-area mt-4">
                        <Button
                            type="button"
                            variant="contained"
                            className="btn-primary"
                            onClick={() => addFormFields()}
                            sx={{
                                width: "100%",
                                maxWidth: "300px",
                                m: "auto"
                            }}
                            startIcon={<AddIcon/>}
                        >
                            Add more
                        </Button>
                    </div>
                </Div>}
        </>
    );
};

export default RelatedMatters;